import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { audioEngine } from '../services/audioEngine';
import { API_BASE, IS_GUEST, guestEmail, setGuestEmail, edgeInset } from '../config';
import useAvatarVoiceChat from '../hooks/useAvatarVoiceChat';

// GuestChatDock — email-gated chat for logged-out visitors. PRIMARY mode is
// REALTIME VOICE (the same ElevenLabs agent as logged-in, via the guest email-
// capped endpoints — hold-to-talk). FALLBACK is text (POST /api/chat/guest, reply
// spoken in the avatar's voice) — offered via "Type instead" and auto-selected if
// the mic is blocked. Music mutes while open.
// THE WALL POINTS INTO THE WORLD, NOT OUT OF IT (Khari, 2026-08-02): a guest
// who runs out of free chat mid-walk-through used to get shipped to the
// website — abandoning the world they were in the middle of being sold. The
// account now happens IN HERE, at the salon (GuestClaim) — but the CTA
// respects the SEQUENCE (found live: it sent Khari to Sassy's before he'd
// ridden): before the call it says stay close, after the call it points at
// Spor1's Lotus, and only after the ride does it glide to the salon.
import { streetPoseFor } from '../rooms';
import { SPOR1_CAR, INTRO_RIDE } from '../worldRide';
import { camBasis } from '../services/worldObjects';

const wrap = {
  position: 'fixed', right: edgeInset(16, 'right'), bottom: 'max(16px, env(safe-area-inset-bottom))',
  zIndex: 70, width: 'min(360px, calc(100vw - 32px))', maxHeight: 'min(560px, 82vh)',
  display: 'flex', flexDirection: 'column', pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif',
  background: 'rgba(16,10,6,0.94)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 16,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};
const header = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid rgba(255,178,94,0.2)' };
const iconBtn = { cursor: 'pointer', background: 'none', border: 'none', color: '#ffe9c4', fontSize: 20, lineHeight: 1, padding: 4 };
const inputStyle = { width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid rgba(255,178,94,0.3)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 15, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
const primaryBtn = { width: '100%', marginTop: 10, padding: '12px', borderRadius: 10, border: 'none', cursor: 'pointer', font: '700 15px system-ui', background: 'linear-gradient(180deg,#ffb25e,#ff5e78)', color: '#1a0f00' };
const switchLink = { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,178,94,0.85)', font: '600 12.5px system-ui', textDecoration: 'underline', padding: 4 };
const PHASE_COLOR = { idle: '#ffb25e', connecting: '#ffd27d', listening: '#ff5e78', thinking: '#ffd27d', speaking: '#7dd3ff', error: '#ff8a9c' };

export default function GuestChatDock() {
  const chatAvatar = useWorldStore((s) => s.chatAvatar);
  const closeChat = useWorldStore((s) => s.closeChat);
  const [email, setEmail] = useState(() => guestEmail());
  const [emailDone, setEmailDone] = useState(() => !!guestEmail());
  const [textMode, setTextMode] = useState(false);

  // text-fallback state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [limitHit, setLimitHit] = useState(false);
  const endRef = useRef(null);
  const audioRef = useRef(null);

  const { phase, caption, error, toggleTalk, end, requestingMic, micDenied } = useAvatarVoiceChat({
    avatarId: chatAvatar?.avatarId, guest: true, email,
  });

  // Mic blocked → drop to the text fallback automatically.
  useEffect(() => { if (micDenied) setTextMode(true); }, [micDenied]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);
  useEffect(() => { setMessages([]); setLimitHit(false); try { audioRef.current?.pause(); } catch { /* noop */ } }, [chatAvatar?.avatarId]);

  // Mute the district music while the chat is open; restore on close.
  useEffect(() => {
    if (!IS_GUEST || !chatAvatar) return undefined;
    const wasOn = useWorldStore.getState().musicOn;
    if (wasOn) { audioEngine.setMusicOn(false).catch(() => {}); useWorldStore.setState({ musicOn: false }); }
    return () => { if (wasOn) { audioEngine.setMusicOn(true).catch(() => {}); useWorldStore.setState({ musicOn: true }); } };
  }, [chatAvatar?.avatarId]);

  // End the voice session + stop any TTS on close / avatar switch.
  useEffect(() => () => { try { end(); } catch { /* noop */ } try { audioRef.current?.pause(); } catch { /* noop */ } }, [chatAvatar?.avatarId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ABOVE the early return — hooks must run every render (this subscription
  // sat below it and crashed the dock the moment it opened: "Rendered more
  // hooks than during the previous render").
  const gp = useWorldStore((s) => s.guestProgress);

  // TAPPING SPOR1 *IS* THE PICKUP (Khari: boarding "should be triggered in
  // conversation" — not by standing on the one magic spot near spawn). While
  // his ride is the open errand, opening a chat with him routes straight into
  // the ride scene instead — which opens with him talking ("It's open — get
  // in"), so the conversation the player asked for is exactly what they get,
  // with the car taking the camera. The kerb pickup card stays for players
  // who walk to the car without tapping the man.
  useEffect(() => {
    if (!IS_GUEST || !chatAvatar) return;
    const st = useWorldStore.getState();
    const gpNow = st.guestProgress || {};
    if (!gpNow.arrivalDone || gpNow.rideDone) return;
    if (!/spor\s*1/i.test(chatAvatar.name || '')) return;
    st.closeChat();
    st.openScene({ key: 'act2_ride_spor1', title: "Spor1's ride", quest: null, room: null });
    st.startRide({ sceneKey: 'act2_ride_spor1', pace: INTRO_RIDE.pace });
  }, [chatAvatar?.avatarId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!IS_GUEST || !chatAvatar) return null;
  const name = chatAvatar.name || 'this artist';
  const first = name.split(' ')[0] || name;

  const submitEmail = (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!/\S+@\S+\.\S+/.test(v)) return;
    setGuestEmail(v);
    setEmailDone(true);
  };

  // ── Text fallback: send + speak the reply in the avatar's voice ──
  const speak = async (text) => {
    if (!text) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/guest/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), avatarId: chatAvatar.avatarId, text }),
      });
      if (!res.ok) return;
      const url = URL.createObjectURL(await res.blob());
      try { audioRef.current?.pause(); } catch { /* noop */ }
      const a = new Audio(url); audioRef.current = a; a.onended = () => URL.revokeObjectURL(url); a.play().catch(() => {});
    } catch { /* silent */ }
  };
  const sendText = async () => {
    const msg = input.trim();
    if (!msg || sending || limitHit) return;
    setInput(''); setSending(true);
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    try {
      const history = messages.slice(-9).map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API_BASE}/api/chat/guest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), avatarId: chatAvatar.avatarId, message: msg, history }),
      });
      const data = await res.json();
      if (res.status === 403 && data.requiresUpgrade) {
        setLimitHit(true);
        setMessages((m) => [...m, { role: 'assistant', content: stepLine, cta: true }]);
      } else if (data.success && data.reply) {
        setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
        speak(data.reply);
      } else {
        setMessages((m) => [...m, { role: 'assistant', content: 'Sorry — I had trouble responding. Try again.', error: true }]);
      }
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Something went wrong. Try again.', error: true }]);
    } finally { setSending(false); }
  };

  const close = () => { try { end(); } catch { /* noop */ } closeChat(); };

  // The conversion glide — SEQUENCE-AWARE, and declared BEFORE anything that
  // reads it (`status` below uses stepLine; it used to sit above this block
  // and crashed the wall render on a const temporal dead zone). Where the
  // wall sends you is where the story actually wants you next, never a skip
  // ahead. (`gp` subscribed above the early return, with the other hooks.)
  const step = !gp?.arrivalDone ? 'call' : !gp?.rideDone ? 'ride' : 'salon';
  // THE REAL CAR — the city's parked Elise (store.landmark), never the stale
  // authored fallback: the old coords pointed at the deleted duplicate's
  // kerb, so the CTA warped people to an empty spot. And if they're ALREADY
  // standing at the car (Khari was, when the wall told him to go find it),
  // the errand collapses to the only sensible button: get in.
  const lm = useWorldStore.getState().landmark;
  const carX = lm ? lm[0] : SPOR1_CAR.pos[0];
  const carZ = lm ? lm[2] : SPOR1_CAR.pos[1];
  // "Found him" has TWO proofs, and the stronger one needs no coordinates:
  // if the avatar being talked to IS Spor1, the errand is complete by
  // definition — proximity math (which silently trusted `landmark` to be
  // set) kept telling Khari to go find the man he was talking to.
  const talkingToSpor1 = /spor\s*1/i.test(name || '');
  const atCar = step === 'ride'
    && (talkingToSpor1
      || Math.hypot(carX - camBasis.px, carZ - camBasis.pz) <= SPOR1_CAR.reach + 3);
  if (step === 'ride') {
    console.log(`[ride] wall check — talkingTo:"${name}" atCar:${atCar} landmark:${lm ? `[${lm[0].toFixed(1)}, ${lm[2].toFixed(1)}]` : 'NULL (fallback in use!)'} cam:[${camBasis.px.toFixed(1)}, ${camBasis.pz.toFixed(1)}]`);
  }
  const stepCta = step === 'call' ? 'Alright'
    : step === 'ride' ? (atCar ? '🟡 Get in' : '🟡 Take me to Spor1')
    : "💇 Make it official at Sassy's";
  const stepLine = step === 'call'
    ? "That's the free talk for now — stay close. Somebody's about to call you."
    : step === 'ride'
      ? (atCar
        ? "That's the free talk out here — but you're standing at the car. Get in; the ride IS the conversation."
        : "That's the free talk for now. Your ride's still waiting — yellow Lotus, main strip. Everything changes after that.")
      : "That's the free talk for now. Want unlimited? Make it official at Sassy's salon — everything you've done out here comes with you.";
  const goNext = () => {
    close();
    const st = useWorldStore.getState();
    if (step === 'ride') {
      if (atCar) {
        // No glide to where you already are — the door just opens. Guest-only
        // surface, so the interview variant is always right.
        st.openScene({ key: 'act2_ride_spor1', title: "Spor1's ride", quest: null, room: null });
        st.startRide({ sceneKey: 'act2_ride_spor1', pace: INTRO_RIDE.pace });
      } else {
        st.setWalkTarget({ pos: [carX + 4, 0, carZ + 4], look: [carX, 0, carZ] });
      }
    } else if (step === 'salon') {
      const pose = streetPoseFor('salon');
      if (pose) st.setWalkTarget({ pos: pose.pos, look: pose.look, enter: 'salon' });
    }
    // step 'call': nothing to fly to — the phone comes to them.
  };

  const upgrade = !!error && /free|sign up|talking/i.test(error);
  // The wall speaks with ONE voice: the API's copy says "sign up" (right for
  // the website), but in the world the next step is the story's — the status
  // must agree with the button above it.
  const status = requestingMic ? 'Allow mic access to talk…'
    : upgrade ? stepLine
    : ({ idle: `Tap to talk to ${first}`, connecting: 'Connecting…', listening: 'Listening…', thinking: 'Thinking…', speaking: `${first} is speaking…`, error: error || '' }[phase] || `Tap to talk to ${first}`);

  return (
    <div style={wrap}>
      <div style={header}>
        <span style={{ color: '#ffd27d', fontWeight: 700, fontSize: 15 }}>{name}</span>
        <button style={iconBtn} onClick={close} aria-label="Close">×</button>
      </div>

      {!emailDone ? (
        <form onSubmit={submitEmail} style={{ padding: 20, textAlign: 'center', color: '#ffe9c4' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🎙</div>
          <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Talk with {first}</div>
          <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 16 }}>Enter your email to start chatting.</div>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" autoFocus style={inputStyle} />
          <button type="submit" style={primaryBtn}>Start</button>
          <div style={{ fontSize: 10.5, opacity: 0.5, marginTop: 10 }}>We’ll only use it to save your chat and send updates.</div>
        </form>
      ) : textMode ? (
        // ── TEXT FALLBACK ──
        <>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 160 }}>
            {micDenied && messages.length === 0 && (
              <div style={{ color: 'rgba(255,178,94,0.85)', fontSize: 12.5, textAlign: 'center', marginTop: 8 }}>Mic is blocked — you can type instead.</div>
            )}
            {messages.length === 0 && !micDenied && (
              <div style={{ color: 'rgba(255,233,196,0.55)', textAlign: 'center', fontSize: 13, marginTop: 20 }}>Say hi to {first} 👋</div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                <div style={{ padding: '8px 12px', borderRadius: 14, fontSize: 14, lineHeight: 1.4,
                  background: m.role === 'user' ? 'linear-gradient(180deg,#ffb25e,#ff8a4c)' : 'rgba(255,255,255,0.08)',
                  color: m.role === 'user' ? '#1a0f00' : (m.error ? '#ffb0bd' : '#ffe9c4'),
                  border: m.role === 'user' ? 'none' : '1px solid rgba(255,178,94,0.2)' }}>{m.content}</div>
                {m.cta && <button onClick={goNext} style={{ ...primaryBtn, width: 'auto', marginTop: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer', border: 'none' }}>{stepCta}</button>}
              </div>
            ))}
            {sending && <div style={{ alignSelf: 'flex-start', color: 'rgba(255,233,196,0.5)', fontSize: 13, padding: '4px 6px' }}>…</div>}
            <div ref={endRef} />
          </div>
          <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid rgba(255,178,94,0.2)' }}>
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }}
              placeholder={limitHit ? "Sassy's takes it from here" : `Message ${first}…`}
              disabled={sending || limitHit} maxLength={500} style={{ ...inputStyle, flex: 1 }} />
            <button onClick={sendText} disabled={!input.trim() || sending || limitHit}
              style={{ padding: '0 16px', borderRadius: 10, border: 'none', cursor: 'pointer', font: '700 14px system-ui', background: (!input.trim() || sending || limitHit) ? 'rgba(255,255,255,0.12)' : '#ffb25e', color: '#1a0f00' }}>↑</button>
          </div>
          {!micDenied && (
            <div style={{ textAlign: 'center', paddingBottom: 8 }}>
              <button style={switchLink} onClick={() => setTextMode(false)}>🎙 Talk instead</button>
            </div>
          )}
        </>
      ) : (
        // ── VOICE (primary) ──
        <div style={{ padding: '22px 20px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          {caption ? (
            <div style={{ maxHeight: 120, overflowY: 'auto', fontSize: 14, lineHeight: 1.45, color: '#ffe9c4', textAlign: 'center' }}>{caption}</div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'rgba(255,233,196,0.55)', textAlign: 'center' }}>Tap the mic, then just talk 👋</div>
          )}

          {!upgrade ? (
            <button
              onClick={toggleTalk}
              style={{ width: 92, height: 92, borderRadius: '50%', cursor: 'pointer', userSelect: 'none', touchAction: 'none',
                fontSize: 34, color: '#1a0f00', border: 'none',
                background: `radial-gradient(circle at 50% 40%, ${PHASE_COLOR[phase] || '#ffb25e'}, #ff8a4c)`,
                boxShadow: `0 8px 30px ${PHASE_COLOR[phase] || '#ffb25e'}66`,
                transition: 'background 0.25s ease, box-shadow 0.25s ease',
                animation: (phase === 'listening' || requestingMic) ? 'gcPulse 1s ease-in-out infinite' : 'none' }}
            >🎙</button>
          ) : (
            <button onClick={goNext} style={{ ...primaryBtn, width: 'auto', padding: '12px 22px', cursor: 'pointer', border: 'none' }}>{stepCta}</button>
          )}

          <div style={{ fontSize: 12.5, letterSpacing: 0.3, color: phase === 'error' ? '#ff8a9c' : 'rgba(255,233,196,0.75)', textAlign: 'center', minHeight: 16 }}>{status}</div>
          {!upgrade && <button style={switchLink} onClick={() => setTextMode(true)}>⌨ Type instead</button>}
        </div>
      )}
      <style>{`@keyframes gcPulse { 0%,100% { transform: scale(1) } 50% { transform: scale(1.06) } }`}</style>
    </div>
  );
}
